import {action, makeAutoObservable, observable, reaction} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {Bindings} from "data/constants/bindings";
import type {
	IColumns,
	IRankTableCell,
} from "views/components/rankings_table/rankings_table.component";
import type {IRankingFilter, IRankingsStore} from "data/stores/rankings/rankings.store";
import type {IRound, IRoundsStore} from "data/stores/rounds/rounds.store";
import type {IUserStore} from "data/stores/user/user.store";
import type {IRankings} from "data/providers/api/rankings.api.provider";
import {isEmpty} from "lodash";
import {SelectChangeEvent} from "@mui/material";

interface IProps {
	leagueId: number;
	isMobile: boolean;
}

export interface ILeagueStandingsController extends ViewController<IProps> {
	readonly i18n: ILocalizationStore;

	get list(): IRankings[];
	get userRank(): IRankings | null;
	get showUserRank(): boolean;
	get tableData(): IRankTableCell[];
	get userId(): number;
	get isNextPage(): boolean;
	get gameDays(): IRound[];
	get rankingFilter(): IRankingFilter;
	get displayedColumns(): IColumns[];
	get selectedColumn(): string;

	onLoadMore: () => void;
	handleSelectChange: (event: SelectChangeEvent) => void;
	setIsMobile: (isMobile: boolean) => void;
}

@injectable()
export class LeagueStandingsController implements ILeagueStandingsController {
	@observable private _leagueId!: number;
	@observable private _isMobile!: boolean;
	private _disposerReaction!: ReturnType<typeof reaction>;

	@observable private _displayedColumn: string = "overallPoints";
	private _displayedColumns: IColumns[] = [
		{
			heading: "Total Pts",
			fieldName: "overallPoints",
		},
		{
			heading: "Day Pts",
			fieldName: "points",
		},
	];

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.RankingsStore) private _rankingsStore: IRankingsStore,
		@inject(Bindings.RoundsStore) private _roundsStore: IRoundsStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore
	) {
		makeAutoObservable(this);
	}

	private get _tableData() {
		return [
			{
				heading: "Rank",
				fieldName: this.isGamedayRanking ? "rank" : "overallRank",
				align: "left",
				style: "first",
			},
			{
				heading: "Name",
				fieldName: "userName",
				align: "left",
				style: "ellipsis",
			},
			{
				heading: "Day Pts",
				fieldName: "points",
				align: "center",
				style: "common",
				sortable: true,
				filterable: true,
			},
			{
				heading: "Total Pts",
				fieldName: "overallPoints",
				align: "center",
				style: "common",
				sortable: true,
				filterable: true,
			},
		] as IRankTableCell[];
	}

	get selectedColumn() {
		return this._displayedColumn;
	}

	get displayedColumns() {
		return this._displayedColumns;
	}

	get isMobile() {
		return this._isMobile;
	}

	get isGamedayRanking() {
		return Boolean(Number(this.rankingFilter.gamedayId));
	}

	get tableData() {
		if (!this.isMobile) {
			return this._tableData;
		}

		return this._tableData.filter((column) => {
			if (!column.filterable) {
				return true;
			}

			return column.fieldName === this._displayedColumn;
		});
	}

	get list(): IRankings[] {
		return this._rankingsStore.list;
	}

	get userRank(): IRankings | null {
		return this._rankingsStore.userRank;
	}

	get isNextPage() {
		return this._rankingsStore.isNextPage;
	}

	get showUserRank(): boolean {
		return (
			Boolean(this.userRank) &&
			!this._rankingsStore.list.find((rank) => rank.userId === this.userRank?.userId)
		);
	}

	get userId() {
		return this._userStore.user?.id || 0;
	}

	get gameDays() {
		return this._roundsStore.completedRounds;
	}

	get rankingFilter() {
		return this._rankingsStore.rankingFilter;
	}

	onLoadMore = () => {
		void this._rankingsStore.fetchMoreLeagueRanking(this._leagueId);
	};

	@action handleSelectChange = (event: SelectChangeEvent) => {
		const {value, name} = event.target;

		if (name === "gamedayId") {
			this._rankingsStore.setFilter(value, name);
		}

		if (name === "displayField") {
			this._displayedColumn = value;
		}
	};

	@action setIsMobile = (isMobile: boolean) => {
		this._isMobile = isMobile;
	};

	dispose() {
		this._disposerReaction?.();
	}
	init({leagueId, isMobile}: IProps) {
		this._leagueId = leagueId;
		this._isMobile = isMobile;

		if (isEmpty(this._roundsStore.list)) {
			void this._roundsStore.fetchRounds();
		}

		void this._rankingsStore.fetchLeagueRanking(this._leagueId);

		this._disposerReaction = reaction(
			() => this._rankingsStore.rankingFilter.gamedayId,
			() => {
				void this._rankingsStore.fetchLeagueRanking(this._leagueId);
			},
			{fireImmediately: false}
		);
	}
}
